import { Box, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api/client';
import { env } from '@/config/env';
import { formatJalaliDate, toPersianDigits } from '@/lib/format/persian';

interface SystemInfo {
  version: string;
  releaseDate: string;
  environment: string;
  serverTimeUtc: string;
}

/**
 * The version and release-date stamp shown at the bottom of every page.
 *
 * @remarks
 * A product requirement, and a genuinely useful one: when somebody reports a problem, the
 * first question is always which build they are looking at, and the answer is on the screen
 * they are already describing.
 *
 * The endpoint is anonymous by design, so this renders on the sign-in page too - which is
 * exactly where a version is most often needed and least often available.
 *
 * The version is read from the server rather than baked into the bundle. A cached bundle
 * paired with a newer API would otherwise report the build the browser happens to be
 * holding, which is the one number nobody wants to be wrong.
 */
export function AppFooter() {
  const { data } = useQuery({
    queryKey: ['system-info'],
    queryFn: () => apiRequest<SystemInfo>('/system-info', { anonymous: true }),

    // It changes only on deployment, so re-fetching it on every focus is pure noise.
    staleTime: 15 * 60 * 1000,
    retry: 1,
  });

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        py: 1.5,
        // Logical properties throughout, so RTL needs no separate rules
        // (frontend-standards.md section 0).
        paddingInline: { xs: 2, md: 3 },
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',

        // The public host name is one long unbreakable token. Without this it sets a floor
        // on the document width and scrolls every page sideways on a narrow screen.
        overflowWrap: 'anywhere',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0.5, sm: 2 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography variant="caption" color="text.secondary">
          سامانه کنترل پروژه ·{' '}
          <Link href={`https://${env.publicHost}`} color="inherit">
            {env.publicHost}
          </Link>
        </Typography>

        {data ? (
          <Tooltip title={`محیط: ${data.environment}`}>
            <Typography variant="caption" color="text.secondary" className="tabular">
              نسخه {toPersianDigits(data.version)} · انتشار {formatJalaliDate(data.releaseDate)}
            </Typography>
          </Tooltip>
        ) : (
          // No skeleton and no spinner. The footer is chrome; a shimmer here draws the eye
          // to the least important thing on the page.
          <Typography variant="caption" color="text.disabled">
            نسخه —
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
